import React, { useCallback } from 'react';
import { SearchType } from '../../../data/__generated__/globalTypes';
import {ForecastingSettingsValue, useForecastingSettings } from '../../ForecastingContextProvider';
import { useApolloClient, useQuery } from '@apollo/client';
import { AutoCompleteQuery, AutoCompleteQueryVariables } from '../../../data/__generated__/AutoCompleteQuery';
import { AUTOCOMPLETE_QUERY, FILTER_VALIDATION_QUERY } from '../../../data/ForecastingResults';
import SSP from './SSP';
import Country from './Country';
import State from './State';
import DMA from './DMA';
import City from './City';
import Zip from './Zip';
import Publisher from './Publisher';
import CreativeTypes from './CreativeTypes';
import DateRange from './DateRange';
import TimeOfWeek from './TimeOfWeek';
import VenueType from './VenueType';
import { WinRate } from './WinRate';
import Collapsible from './Collapsible';
import PointsOfInterest from './PointsOfInterest';

export const selectDefaults = (values: string[] | null) => {
    return values == null ? [] : values.map(v => {
        return { label: v, value: v };
    });
};

function getSearchTerms(type: SearchType, forecastingSettings: ForecastingSettingsValue) {
    switch(type) {
        case SearchType.COUNTRY:
            return forecastingSettings.countryFilter ?? [];
        case SearchType.REGION:
            return forecastingSettings.regionFilter ?? [];
        case SearchType.METRO:
            return forecastingSettings.metroFilter?.map( ({label, value}) => label ) ?? [];
        case SearchType.CITY:
            return forecastingSettings.cityFilter ?? [];
        case SearchType.ZIP:
            return forecastingSettings.zipFilter ?? [];
        case SearchType.PUBLISHER:
            return forecastingSettings.publisherFilter ?? [];
        case SearchType.VENUE_TYPE:
            return forecastingSettings.venueTypeFilter?.map( ({label, value}) => label ) ?? [];
        default:
            return [];
    }
}
export function useForecastingFilterValidation(type: SearchType) {
    const { forecastingSettings } = useForecastingSettings();
    return useQuery(FILTER_VALIDATION_QUERY, {
        skip: getSearchTerms(type, forecastingSettings).length === 0 ? true : false,
        variables: {
            type,
            searchTerms: getSearchTerms(type, forecastingSettings),
            ssp: forecastingSettings.ssp,
            publisher: type === SearchType.PUBLISHER ? null : forecastingSettings.publisherFilter,
            venueType: type === SearchType.VENUE_TYPE ? null : forecastingSettings.venueTypeFilter?.map(it => it.value),
            country: type === SearchType.COUNTRY ? null : forecastingSettings.countryFilter,
            region: type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.regionFilter,
            metro: type === SearchType.METRO || type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.metroFilter?.map(it => it.value),
            city: type === SearchType.CITY  || type === SearchType.METRO || type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.cityFilter,
            zip: type === SearchType.ZIP || type === SearchType.CITY  || type === SearchType.METRO || type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.zipFilter
        }
    })
}

export function useAutoCompleteOptions(type: SearchType): (
    inputValue: string,
    callback: (options: { label: string; value: string }[]) => void
) => void {
    const { forecastingSettings } = useForecastingSettings();
    const apolloClient = useApolloClient();

    return useCallback((
        inputValue: string,
        callback: (options: { label: string, value: string }[]) => void
    ) => {
        apolloClient.query<AutoCompleteQuery, AutoCompleteQueryVariables>({
            query: AUTOCOMPLETE_QUERY,
            variables: {
                type,
                ssp: forecastingSettings.ssp,
                limit: 100,
                searchTerm: inputValue,
                publisher: type === SearchType.PUBLISHER ? null : forecastingSettings.publisherFilter,
                venueType: type === SearchType.VENUE_TYPE ? null : forecastingSettings.venueTypeFilter?.map(it => it.value),
                country: type === SearchType.COUNTRY ? null : forecastingSettings.countryFilter,
                region: type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.regionFilter,
                metro: type === SearchType.METRO || type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.metroFilter?.map(it => it.value),
                city: type === SearchType.CITY  || type === SearchType.METRO || type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.cityFilter,
                zip: type === SearchType.ZIP || type === SearchType.CITY  || type === SearchType.METRO || type === SearchType.REGION || type === SearchType.COUNTRY ? null : forecastingSettings.zipFilter
            }
        }).then(result => {
            callback(result?.data?.populateAutoComplete?.entries?.map(c => {
                return { value: c.value, label: c.label };
            }) || []);
        });
    }, [forecastingSettings]);
}
export default function ForecastingFilters() {
    return (
        <div className='overflow-scroll' style={{maxHeight: '100%'}}>
            <SSP />
            <Collapsible collapsed={true} title={'List Targeting'}>
                <PointsOfInterest />
            </Collapsible>
            <Collapsible collapsed={true} title={'Geo'}>
                <Country />
                <State />
                <DMA />
                <City />
                <Zip />
                <VenueType />
            </Collapsible>
            <Collapsible collapsed={true} title={'Partners'}>
                <Publisher />
            </Collapsible>
            <Collapsible collapsed={true} title={'Time'}>
                <DateRange />
                <TimeOfWeek />
            </Collapsible>
            <Collapsible collapsed={true} title={'Technical specifications'}>
                <CreativeTypes />
                <WinRate />
            </Collapsible>
        </div>
    );
}
